import { useState } from 'react';
import { ListPlus, TriangleAlert } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import RoleGate from '../../components/RoleGate';

// NestJS sends `message` as a string, or as an array of them for validation errors
function errorText(err, fallback) {
  const message = err.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return (typeof message === 'string' && message) || err.message || fallback;
}

// Shown on the Tests card of an epic with no test case, to write and admin users. quality-service passes
// the request on to QA Compass (ai-quality-tools-api), which creates the Jira Test Cases in about a minute
// and skips ones that already exist. Nothing tracks the run: the test cases show here after the next Jira
// sync. The page renders it with key={epicKey}, so a request still pending for the previous epic can only
// update the unmounted instance.
function TestCaseGeneration({ epicKey }) {
  const [state, setState] = useState('idle'); // idle | sending | requested
  const [error, setError] = useState('');

  const generate = async () => {
    setState('sending');
    setError('');
    try {
      await api.post(`${QUALITY_SERVICE_API_URL}/hub/software-test-report/${encodeURIComponent(epicKey)}/test-cases`);
      setState('requested');
    } catch (err) {
      setState('idle');
      setError(errorText(err, 'Could not start the test case generation'));
    }
  };

  return (
    <RoleGate allow={['admin', 'write']}>
      <div className="str-test-case-generate">
        {state === 'requested' ? (
          <p className="str-stp-status" role="status">
            Requested: QA Compass is creating the test cases in Jira. They show here after the next sync. They are
            AI-generated: review each one before relying on it.
          </p>
        ) : (
          <>
            <button type="button" className="str-link str-stp-button" onClick={generate} disabled={state === 'sending'}>
              <ListPlus size={16} /> {state === 'sending' ? 'Starting…' : 'Generate test cases'}
            </button>
            <p className="str-ai-warning">
              <TriangleAlert size={14} aria-hidden="true" />
              <span>
                QA Compass creates AI-generated test cases directly in Jira. Review and correct each one before relying
                on it.
              </span>
            </p>
          </>
        )}
        {error && (
          <p className="str-epic-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </RoleGate>
  );
}

export default TestCaseGeneration;
